import { Router, CanActivateFn } from '@angular/router';
import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@/app/core/services/auth.service';
import { NotificationService } from '@/app/core/services/notification.service';
import { errorDeAplicacionDe } from '@/app/core/errores/error-de-aplicacion';
import { inject } from '@angular/core';

/**
 * Whether the console's own origin answers (`/version.json`, which the deploy writes next to the
 * console and the same nginx serves). Asked when `/auth/me` got no response at all (status 0): if
 * the console answers, the operator's network works and it is the API that does not —restarting, or
 * an error of nginx that leaves without CORS, which the browser hides as status 0 (AB-650 gives
 * those errors CORS: then they arrive as what they are)—. Without the interceptors (no token to the
 * static server) and without the HTTP cache. Any answer, a 404 too, is an answer.
 */
function laConsolaContesta(consola: HttpClient): Promise<boolean> {
  return firstValueFrom(
    consola.get('/version.json', { headers: { 'Cache-Control': 'no-cache' } }),
  ).then(
    () => true,
    (err: HttpErrorResponse) => err.status > 0,
  );
}

/** The check in flight and the URL sign-in returns to: the one of the last navigation that joined. */
interface Comprobacion {
  url: string;
  listo?: Promise<boolean>;
}

/**
 * The check in flight, by `AuthService` (one per injector, so a test module never finds the one of
 * the previous). A navigation that starts while another one waits —back or forward of the browser
 * during a restart of the API— joins it: one loop, one notice, one request per attempt. Until 10-17
 * each navigation started its own loop with its own notice, and the loop of the cancelled one kept
 * asking in the background.
 */
const EN_CURSO = new WeakMap<AuthService, Comprobacion>();

/**
 * Functional Auth Guard
 * Can be used directly in route definitions
 *
 * A stored session is closed ONLY when the server rejects it: a 401 or a 403 from `/auth/me` that
 * the refresh token could not fix, or a 404 —the person or the account no longer exists— the refresh
 * could not fix either (`AuthService.restoreSession`). Until 10-17 any failure of `/auth/me` closed
 * it —reloading, a new tab or a direct link during a restart of the API, a 502 of the proxy, a
 * dropped connection— and threw the person to sign-in with both tokens erased.
 *
 * Any other failure keeps the tokens and says so in a notice that stays, with «Ir al ingreso» —no
 * wait is a trap—, and what follows depends on whose failure it is:
 *
 * - a server that could not answer (no response, a 5xx, a 408 or a 429) is asked again with a growing
 *   wait (1 s, 2 s, 4 s… up to 30 s), and the notice says the session is still open. With no response
 *   at all it does not blame the operator's network unless it knows: the browser says it is offline,
 *   or the console's own origin does not answer either (`laConsolaContesta`) and then it says only
 *   that it could not reach the server;
 * - a definitive answer (any other 4xx: a 402 of a suspended account, the 400 of a refresh that
 *   failed) is NOT asked again: asking would give the same answer forever. The notice says what
 *   happened in the console's words —the core's are «Token refresh failed» or voseo— and stays until
 *   the person leaves. Reloading asks again.
 *
 * «Ir al ingreso» closes the session and goes to sign-in; if a request was in flight, as soon as it
 * settles, so that its late answer can neither let the person in nor leave a session behind. The loop
 * also ends when the session does elsewhere.
 */
export const authGuard: CanActivateFn = (_route, state) => {
  const authService = inject(AuthService);

  if (authService.isAuthenticated()) {
    return true;
  }

  let enCurso = EN_CURSO.get(authService);
  if (enCurso) {
    enCurso.url = state.url;
  } else {
    // Without the interceptors: it asks the console's own origin, never the API (see the gate of
    // the support mode, `el-soporte-escribe-en-la-cuenta-que-atiende.spec.ts`).
    const consola = new HttpClient(inject(HttpBackend));
    EN_CURSO.set(authService, (enCurso = { url: state.url }));
    enCurso.listo = comprobar(
      authService,
      inject(Router),
      inject(NotificationService),
      consola,
      enCurso,
    ).finally(() => EN_CURSO.delete(authService));
  }
  return enCurso.listo as Promise<boolean>;
};

async function comprobar(
  authService: AuthService,
  router: Router,
  notification: NotificationService,
  consola: HttpClient,
  enCurso: Comprobacion,
): Promise<boolean> {
  let shown = '';
  let closeNotice = (): void => undefined;
  let wake = (): void => undefined;
  let left = false;
  const leave = (): void => {
    left = true;
    closeNotice();
    authService.clearSession();
    wake();
  };
  for (let attempt = 0; authService.getToken(); attempt++) {
    try {
      const valid = await authService.restoreSession();
      if (left) break;
      if (valid) {
        closeNotice();
        return true;
      }
      authService.expireSession(notification);
    } catch (err) {
      // Not the request id of the failed request: each retry gets a new one, and the notice would
      // be replaced at every attempt.
      const { estadoHttp: status, mensaje } = errorDeAplicacionDe(err);
      const ofTheMoment = !status || status > 499 || status === 408 || status === 429;
      const cause = status
        ? mensaje
        : !navigator.onLine
          ? 'No tienes conexión a internet.'
          : (await laConsolaContesta(consola))
            ? 'El servidor no responde; puede estar reiniciándose.'
            : mensaje;
      if (left) break;
      const text = ofTheMoment
        ? `${cause} Tu sesión sigue abierta y volvemos a intentarlo.`
        : status === 402
          ? 'Tu cuenta está suspendida por falta de pago. Podrás entrar cuando se regularice.'
          : 'No pudimos abrir tu sesión.';
      if (text !== shown) {
        closeNotice();
        closeNotice = notification.persistent((shown = text), 'Ir al ingreso', leave);
      }
      await new Promise<void>((resolve) => {
        wake = resolve;
        // A definitive answer is not asked again: it waits for the person to leave.
        if (ofTheMoment) setTimeout(resolve, Math.min(30_000, 1000 * 2 ** attempt));
      });
    }
  }
  // What a request that was in flight when the person left wrote back (a renewed pair, a verified
  // user) does not stay: they chose to leave.
  if (left) authService.clearSession();
  closeNotice();

  router.navigate(['/sign-in'], {
    queryParams: { returnUrl: enCurso.url },
  });
  return false;
}
